import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getAppointments } from "../services/firestore";

const Charts = () => {
  const [data, setData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const appointments = await getAppointments();
      setData(appointments);
    };
    fetchData();
  }, []);

  const residentialCount = data.filter((d) => d.lookingFor === "Residential").length;
  const commercialCount = data.filter((d) => d.lookingFor === "Commercial").length;

  const pendingCount = data.filter((d) => d.status === "Pending").length;
  const completedCount = data.filter((d) => d.status === "Completed").length;
  const rescheduledCount = data.filter((d) => d.status === "Rescheduled").length;

  const totalLookingFor = residentialCount + commercialCount;
  const totalStatus = pendingCount + completedCount + rescheduledCount;

  const pieData = [
    { name: "Residential", value: residentialCount },
    { name: "Commercial", value: commercialCount },
  ];

  const statusData = [
    { name: "Pending", value: pendingCount },
    { name: "Completed", value: completedCount },
    { name: "Rescheduled", value: rescheduledCount },
  ];

  const COLORS = ["#0088FE", "#FF8042", "#00C49F"];

  const barData = Array.from({ length: 12 }, (_, i) => ({
    month: new Date(0, i).toLocaleString("default", { month: "short" }),
    count: data.filter((d) => new Date(d.todaysDate).getMonth() === i).length,
  }));

  const lineData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateString = date.toISOString().split("T")[0];
    return {
      day: date.toLocaleDateString("default", { weekday: "short" }),
      count: data.filter((d) => d.todaysDate === dateString).length,
    };
  }).reverse();

  return (
    <>
      <h1 className="text-4xl font-bold text-center my-20 flex justify-center items-center text-blue-800">
        Analysis by Charts
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-4">
        {/* Residential vs Commercial Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 transition-transform transform hover:scale-105">
          <h3 className="text-lg font-bold mb-4 text-center text-blue-600">
            Residential vs Commercial
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) =>
                  `${name}: ${(value / totalLookingFor * 100).toFixed(1)}%`
                }
                isAnimationActive={true}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            {pieData.map((entry, index) => (
              <div key={index} className="flex justify-center items-center space-x-2">
                <div
                  style={{
                    backgroundColor: COLORS[index],
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                  }}
                ></div>
                <span>{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Appointments by Month Bar Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 transition-transform transform hover:scale-105">
          <h3 className="text-lg font-bold mb-4 text-center text-blue-600">
            Appointments by Month
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8884d8" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Appointment Status Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 transition-transform transform hover:scale-105">
          <h3 className="text-lg font-bold mb-4 text-center text-blue-600">
            Appointment Status
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) =>
                  `${name}: ${(value / totalStatus * 100).toFixed(1)}%`
                }
                isAnimationActive={true}
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            {statusData.map((entry, index) => (
              <div key={index} className="flex justify-center items-center space-x-2">
                <div
                  style={{
                    backgroundColor: COLORS[index],
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                  }}
                ></div>
                <span>{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Appointments Line Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 transition-transform transform hover:scale-105">
          <h3 className="text-lg font-bold mb-4 text-center text-blue-600">
            Daily Appointments (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={lineData}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#82ca9d" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default Charts;
